import { APP_TIMEZONE, APP_LOCALE } from '../config/timezone';

function pad2(n) {
  return String(n).padStart(2, '0');
}

/**
 * Format wall date as dd-mm-yyyy in Asia/Kolkata.
 * Plain YYYY-MM-DD (DATE fields): no conversion — treat as calendar date.
 */
export function formatCalendarDateDMY(value) {
  if (value == null || value === '') return '—';
  const s0 = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s0)) {
    const [y, m, d] = s0.split('-');
    return `${pad2(Number(d))}-${pad2(Number(m))}-${y}`;
  }
  const d = value instanceof Date ? value : new Date(s0);
  if (Number.isNaN(d.getTime())) return '—';
  const ymd = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
  const [y, m, day] = ymd.split('-');
  return `${pad2(Number(day))}-${pad2(Number(m))}-${y}`;
}

/** Datetime → dd-mm-yyyy HH:mm:ss in Asia/Kolkata (24h) */
export function formatDateTimeDMYIST(value) {
  if (value == null || value === '') return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  // en-CA yields numeric date parts reliably (not month names)
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || '00';
  return `${get('day')}-${get('month')}-${get('year')} ${get('hour')}:${get('minute')}:${get('second')}`;
}

/** Short datetime for tables / tooltips */
export function formatDateTimeShortIST(value) {
  if (value == null || value === '') return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(APP_LOCALE, {
    timeZone: APP_TIMEZONE,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}

/** YYYY-MM-DD for instant `value` in Asia/Kolkata (never UTC midnight shift). */
export function formatYmdIST(value = new Date()) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

export function todayYmdIST() {
  return formatYmdIST(new Date());
}

/** `YYYY-MM-DD` for `<input type="date">` from API (plain DATE or datetime instant in IST). */
export function formatDateForInputIST(value) {
  if (value == null || value === '') return '';
  const s0 = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s0)) return s0;
  const d = value instanceof Date ? value : new Date(s0);
  if (Number.isNaN(d.getTime())) return '';
  return formatYmdIST(d);
}

/** Add calendar days to a date-only YYYY-MM-DD; returns YYYY-MM-DD (no TZ ambiguity). */
export function addCalendarDaysYmd(ymd, deltaDays) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(String(ymd).slice(0, 10))) return '';
  const s = String(ymd).slice(0, 10);
  const [y0, m0, d0] = s.split('-').map(Number);
  const z = new Date(Date.UTC(y0, m0 - 1, d0, 12, 0, 0));
  z.setUTCDate(z.getUTCDate() + (Number(deltaDays) || 0));
  const y = z.getUTCFullYear();
  const m = String(z.getUTCMonth() + 1).padStart(2, '0');
  const day = String(z.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** IST wall calendar for "now" (month 1–12). */
export function getCalendarNowIST() {
  const d = new Date();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { year: get('year'), month: get('month'), day: get('day') };
}

/**
 * Calendar year/month/day in Asia/Kolkata.
 * Plain `YYYY-MM-DD` (DATE fields): use as-is. Instants: IST wall date.
 */
export function getCalendarPartsIST(value) {
  if (value == null || value === '') return null;
  const s0 = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s0)) {
    const [y, m, d] = s0.split('-').map(Number);
    return { year: y, month: m, day: d };
  }
  const d = value instanceof Date ? value : new Date(s0);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { year: get('year'), month: get('month'), day: get('day') };
}

/** Age breakdown from DOB using IST "today" and calendar DOB (aligns with clinic timezone). */
export function calculateDetailedAgeIST(dob) {
  const birth = getCalendarPartsIST(dob);
  const today = getCalendarNowIST();
  if (!birth?.year || !today?.year) return null;

  let years = today.year - birth.year;
  let months = today.month - birth.month;
  let days = today.day - birth.day;

  if (days < 0) {
    months--;
    const jsMonthIndex = today.month - 1;
    days += new Date(today.year, jsMonthIndex, 0).getDate();
  }
  if (months < 0) {
    years--;
    months += 12;
  }

  const parts = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? 'year' : 'years'}`);
  if (months > 0) parts.push(`${months} ${months === 1 ? 'month' : 'months'}`);
  if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  if (parts.length === 0) return '0 days';
  return parts.join(', ');
}

export function formatMonthLongFromIndex0(monthIndex0To11) {
  if (monthIndex0To11 < 0 || monthIndex0To11 > 11) return '';
  return new Intl.DateTimeFormat(APP_LOCALE, {
    timeZone: APP_TIMEZONE,
    month: 'long',
  }).format(new Date(Date.UTC(2000, monthIndex0To11, 15, 12, 0, 0)));
}

export function formatMonthShortFromIndex0(monthIndex0To11) {
  if (monthIndex0To11 < 0 || monthIndex0To11 > 11) return '';
  return new Intl.DateTimeFormat(APP_LOCALE, {
    timeZone: APP_TIMEZONE,
    month: 'short',
  }).format(new Date(Date.UTC(2000, monthIndex0To11, 15, 12, 0, 0)));
}

export function formatMonthLongCalendarMonth(month1To12) {
  return formatMonthLongFromIndex0(month1To12 - 1);
}

/** HH:mm (24h) in Asia/Kolkata for instant `date`. */
export function formatHiMiIST(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '00:00';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  }).formatToParts(d);
  const h = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const m = parts.find((p) => p.type === 'minute')?.value ?? '00';
  return `${h}:${m}`;
}

/**
 * Add minutes to a wall clock on a calendar day in IST (Asia/Kolkata).
 * `dateYmd` = YYYY-MM-DD, `hiMi` = H:mm or HH:mm
 */
export function addMinutesOnWallClockIST(dateYmd, hiMi, deltaMinutes = 0) {
  const ymd = String(dateYmd).slice(0, 10);
  const tm = String(hiMi || '00:00').match(/^(\d{1,2}):(\d{2})/);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd) || !tm) return '00:00';
  const h = Number(tm[1]);
  const min = Number(tm[2]);
  const iso = `${ymd}T${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}:00+05:30`;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '00:00';
  const d2 = new Date(t + (Number(deltaMinutes) || 0) * 60000);
  return formatHiMiIST(d2);
}

export function formatMonthYearIST(value) {
  if (value == null || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(APP_LOCALE, {
    timeZone: APP_TIMEZONE,
    month: 'long',
    year: 'numeric',
  }).format(d);
}

function ordSuffix(n) {
  if (n >= 11 && n <= 13) return 'th';
  switch (n % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

/** e.g. 15th January 2025 in IST */
/** Hour of day 0–23 in Asia/Kolkata (for greetings, business hours). */
export function getCurrentHourOfDayIST(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIMEZONE,
    hour: 'numeric',
    hour12: false,
    hourCycle: 'h23',
  }).formatToParts(date);
  return Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
}

/** e.g. Sun, 19 Apr 2026 in Asia/Kolkata (DATE-only strings anchored at noon IST). */
/** e.g. "Sunday, 19 April" in Asia/Kolkata (for headings). */
export function formatLongWeekdayMonthDayIST(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(APP_LOCALE, {
    timeZone: APP_TIMEZONE,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(d);
}

export function formatDateWeekdayShortIST(value) {
  if (value == null || value === '') return '';
  const s0 = String(value).trim();
  let d;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s0)) {
    d = new Date(`${s0}T12:00:00+05:30`);
  } else {
    d = value instanceof Date ? value : new Date(s0);
  }
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(APP_LOCALE, {
    timeZone: APP_TIMEZONE,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
}

export function formatDateLongOrdinalIST(value) {
  if (value == null || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat(APP_LOCALE, {
    timeZone: APP_TIMEZONE,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).formatToParts(d);
  const dayNum = Number(parts.find((p) => p.type === 'day')?.value);
  const month = parts.find((p) => p.type === 'month')?.value || '';
  const year = parts.find((p) => p.type === 'year')?.value || '';
  if (!dayNum) return '';
  return `${dayNum}${ordSuffix(dayNum)} ${month} ${year}`;
}
